/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  Button,
  CircularProgress,
  Card,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material';
import tenant from '@/services/TenantService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';

export default function ManualSubscriptionForm({
  app,
  users,
  subscriptionsToUpdate,
  usersToDeactivate,
  deploymentId,
  update,
  onFinish,
  onCancel,
}) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  const [tenantData, setTenant] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [paymentError, setPaymentError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const quantity = update
        ? Math.max(
            Math.floor(
              users?.length +
                subscriptionsToUpdate?.length -
                usersToDeactivate?.length,
            ),
            0,
          )
        : Math.max(users?.length, 0);

      try {
        const { data } = await tenant.post('/create-checkout-session', {
          app: app._id,
          quantity: quantity,
          deployment: deploymentId,
        });

        setPriceBreakdown(data.priceBreakdown);
        setPaymentMethods(data.paymentMethods || []);
        setSelectedMethod(
          data.defaultPaymentMethodId || data.paymentMethods?.[0]?.id || null,
        );
        setTotalAmount(data?.totalAmount);
      } catch (error) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle="Fehler beim Laden der Zahlungsdaten"
          />,
        );
        setPaymentError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [app._id, deploymentId, users?.length]);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const { data } = await tenant.get('/tenant');
        if (data.invoice) {
          setSelectedMethod('invoice');
        }
        setTenant(data);
      } catch (error) {
        console.error('Error fetching tenant data:', error);
      }
    };
    fetchTenant();
  }, []);

  const handleSubscribe = async () => {
    if (paymentError) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Die Abrechnung konnte nicht durchgeführt werden, da die Rechnungsdaten nicht geladen werden konnten."
        />,
      );
      return;
    }
    if (!selectedMethod) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Bitte wählen Sie eine Zahlungsmethode aus"
        />,
      );
      return;
    }

    if (tenantData.invoice) {
      toast.success(
        <ToastMessage
          title="Info"
          subtitle="Die Abrechnung über Rechnung wurde eingerichtet."
        />,
        { autoClose: 1000 },
      );
      onFinish();
      return;
    }

    setSubmitting(true);

    try {
      // Not yet finished and tested!!
      if (update) {
        // Update existing subscription
        const resp = await tenant.put('/stripeSubscription', {
          app: app._id,
          users,
          deploymentId,
          paymentMethodId: selectedMethod,
          subscriptionsToUpdate,
          usersToDeactivate,
        });
        if (resp.status === 204) {
          toast.success(
            <ToastMessage
              title="Erfolg "
              subtitle="Ihr Abonnement wurde erfolgreich bearbeitet"
            />,
            { autoClose: 1000 },
          );
          onFinish();
        }
      } else {
        // If we don't update, we create a new subscription
        const response = await tenant.post('/stripeSubscription', {
          app: app._id,
          users,
          deploymentId,
          paymentMethodId: selectedMethod,
        });

        if (response.status === 204) {
          toast.success(
            <ToastMessage
              title="Erfolg"
              subtitle="Ihr Abonnement wurde erfolgreich erstellt"
            />,
            { autoClose: 1000 },
          );
          onFinish();
        } else {
          throw new Error(response.data?.message || 'Unknown error');
        }
      }
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler beim Abonnieren"
          subtitle={
            error.response?.data?.message ||
            error.message ||
            'Ein unbekannter Fehler ist aufgetreten'
          }
        />,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flex: 1,
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Skeleton variant="rectangular" width={'100%'} height={180} />
        <Skeleton variant="rectangular" width={'100%'} height={100} />
        <Skeleton variant="rectangular" width={'100%'} height={100} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        minWidth: 400,
      }}
    >
      {selectedMethod && (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary.main">
            {app.title}
          </Typography>

          {priceBreakdown.map((item, idx) => (
            <Box
              key={idx}
              display="flex"
              justifyContent="space-between"
              sx={{ py: 1 }}
            >
              <Typography>
                {item.label} × {item.quantity}
              </Typography>
              <Typography>{(item.subtotal / 100).toFixed(2)} €</Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography fontWeight="bold">Gesamt:</Typography>
            <Typography fontWeight="bold" color="primary.main">
              {(totalAmount / 100).toFixed(2)} €
            </Typography>
          </Box>
        </Card>
      )}

      {tenantData.invoice ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ihr Konto ist für die Abrechnung per Rechnung eingerichtet. Das
          Abonnement wird entsprechend angepasst.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary.main">
            Zahlungsmethode auswählen
          </Typography>

          <Box sx={{ mt: 2 }}>
            {paymentMethods.length > 0 ? (
              paymentMethods.map((pm) => (
                <Box
                  key={pm.id}
                  sx={{
                    border: '1px solid',
                    borderColor:
                      selectedMethod === pm.id ? 'primary.main' : 'divider',
                    p: 2,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    bgcolor:
                      selectedMethod === pm.id
                        ? 'primary.lighter'
                        : 'background.paper',
                    opacity: selectedMethod === pm.id ? 1 : 0.8,
                    '&:hover': {
                      borderColor: 'primary.main',
                      opacity: 1,
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => setSelectedMethod(pm.id)}
                >
                  <Radio
                    checked={selectedMethod === pm.id}
                    onChange={() => setSelectedMethod(pm.id)}
                    value={pm.id}
                    name="payment-method-radio"
                    sx={{ mr: 1 }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCardIcon sx={{ mr: 1, color: 'action.active' }} />

                      <Typography
                        variant="subtitle1"
                        fontWeight={
                          selectedMethod === pm.id ? 'bold' : 'normal'
                        }
                      >
                        {pm.card.brand.toUpperCase()} •••• {pm.card.last4}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      Gültig bis {pm.card.exp_month}/{pm.card.exp_year}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography color="text.secondary">
                  Keine Zahlungsmethoden verfügbar. Bitte fügen Sie eine
                  Zahlungsmethode hinzu.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={() => {
                    navigate('/admin/paymentMethods');
                  }}
                >
                  Zahlungsmethode hinzufügen
                </Button>
              </Box>
            )}
          </Box>
        </Card>
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubscribe}
          disabled={!selectedMethod || submitting}
          fullWidth
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
          ) : null}
          Jetzt kostenpflichtig abonnieren
        </Button>
      </Box>
      {onCancel && (
        <Button onClick={onCancel} fullWidth sx={{ mt: 2 }}>
          Abbrechen
        </Button>
      )}
    </Box>
  );
}
